import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import MainFooter from '../../components/layout/MainFooter';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Icon from '../../components/AppIcon';
import '../../styles/legal-pages.css';

const CookiesPolicyPage = () => {
  const [expandedSection, setExpandedSection] = useState('what-are-cookies');

  const sections = {
    'what-are-cookies': {
      title: 'What Are Cookies',
      icon: 'FileText',
      content: [
        {
          heading: 'Cookie Definition',
          text: 'Cookies are small text files that are placed on your device when you visit a website. They help websites remember your preferences, settings, and activity to provide a better user experience.'
        },
        {
          heading: 'How We Use Cookies',
          text: 'VA Sprouts uses cookies and similar technologies (such as local storage, session storage, and pixels) to enhance your experience, analyze usage, remember your preferences, and improve our platform.'
        },
        {
          heading: 'Your Control',
          text: 'You have control over cookies and can manage your preferences through your browser settings. However, disabling certain cookies may limit functionality on our platform.'
        }
      ]
    },
    'types-of-cookies': {
      title: 'Types of Cookies We Use',
      icon: 'List',
      content: [
        {
          heading: 'Essential Cookies',
          text: 'These cookies are necessary for the platform to function properly. They enable core functionality such as:\n\n• User authentication and session management\n• Security and fraud prevention\n• Load balancing and performance\n• Remembering your login state\n• Protecting against cross-site request forgery (CSRF)\n\nThese cookies cannot be disabled as they are essential for the platform to work.'
        },
        {
          heading: 'Functional Cookies',
          text: 'These cookies enhance your experience by remembering your choices and preferences:\n\n• Language preferences\n• Dark mode or theme settings\n• Search filters and saved criteria\n• Dashboard layout preferences\n• Notification settings\n• Recently viewed profiles or jobs\n\nYou can disable these cookies, but some features may not work as intended.'
        },
        {
          heading: 'Analytics Cookies',
          text: 'These cookies help us understand how users interact with our platform:\n\n• Google Analytics: Tracks page views, session duration, bounce rates\n• Mixpanel: Analyzes user behavior and feature usage\n• Heap Analytics: Records user interactions and funnels\n• Performance monitoring: Identifies technical issues and slow pages\n\nWe use this data to improve our platform and user experience. You can opt out of analytics cookies through your browser settings.'
        },
        {
          heading: 'Marketing & Advertising Cookies',
          text: 'These cookies are used for marketing purposes:\n\n• Tracking ad campaign performance\n• Retargeting campaigns on platforms like Google Ads and Facebook\n• Conversion tracking for sign-ups and job posts\n• A/B testing different features and designs\n• Social media integration and sharing\n\nYou can opt out of marketing cookies through your browser or our cookie preferences.'
        },
        {
          heading: 'Third-Party Cookies',
          text: 'Some features on our platform use third-party services that may set their own cookies:\n\n• Stripe (payment processing)\n• Google (authentication, analytics, maps)\n• LinkedIn (social login)\n• reCAPTCHA (bot prevention)\n• Customer support chat tools\n\nThese third parties have their own cookie policies, which we encourage you to review.'
        }
      ]
    },
    'specific-cookies': {
      title: 'Cookies We Set',
      icon: 'Database',
      content: [
        {
          heading: 'Authentication Cookies',
          text: 'Cookie Name: sb-access-token, sb-refresh-token\nPurpose: Supabase authentication session management\nDuration: Session or persistent (based on your login choice)\nType: Essential'
        },
        {
          heading: 'Session Cookies',
          text: 'Cookie Name: va_sprouts_session\nPurpose: Maintains your active session on the platform\nDuration: Session (deleted when browser closes)\nType: Essential'
        },
        {
          heading: 'User Preferences',
          text: 'Cookie Name: va_sprouts_prefs\nPurpose: Stores your theme, language, and layout preferences\nDuration: 1 year\nType: Functional'
        },
        {
          heading: 'Analytics Tracking',
          text: 'Cookie Name: _ga, _gid, _gat (Google Analytics)\nPurpose: Anonymously tracks user behavior and usage patterns\nDuration: Up to 2 years\nType: Analytics'
        },
        {
          heading: 'Feature Flags',
          text: 'Cookie Name: va_sprouts_features\nPurpose: Controls which experimental features are shown to you\nDuration: 30 days\nType: Functional'
        },
        {
          heading: 'Marketing Attribution',
          text: 'Cookie Name: va_sprouts_utm, va_sprouts_ref\nPurpose: Tracks which marketing campaigns brought you to our site\nDuration: 90 days\nType: Marketing'
        }
      ]
    },
    'local-storage': {
      title: 'Local Storage & Session Storage',
      icon: 'HardDrive',
      content: [
        {
          heading: 'What Is Local Storage',
          text: 'Local storage and session storage are web technologies similar to cookies that allow websites to store data directly in your browser. Unlike cookies, this data is not sent to our servers with every request.'
        },
        {
          heading: 'How We Use Local Storage',
          text: 'We use local storage and session storage for:\n\n• Caching profile data to reduce loading times\n• Storing draft job posts and applications\n• Remembering your last search results\n• Saving temporary form data to prevent loss\n• Storing UI state (expanded menus, active tabs)\n• Offline functionality for messaging\n• Performance optimization'
        },
        {
          heading: 'Managing Local Storage',
          text: 'You can clear local storage through your browser settings. Note that clearing this data may log you out and reset your preferences.'
        }
      ]
    },
    'third-party-services': {
      title: 'Third-Party Services & Pixels',
      icon: 'ExternalLink',
      content: [
        {
          heading: 'Google Services',
          text: 'We use Google Analytics, Google Ads, and Google Tag Manager. These services may set cookies to track your activity across websites.\n\nOpt-out: Install the Google Analytics Opt-out Browser Add-on\nPrivacy Policy: https://policies.google.com/privacy'
        },
        {
          heading: 'Stripe (Payment Processing)',
          text: 'Stripe uses cookies to detect fraudulent activity and secure payment processing.\n\nPrivacy Policy: https://stripe.com/privacy'
        },
        {
          heading: 'Social Media Pixels',
          text: 'We may use Facebook Pixel, LinkedIn Insight Tag, and Twitter conversion pixels to measure ad effectiveness.\n\nYou can opt out through each platform\'s ad preferences:\n• Facebook: https://www.facebook.com/settings?tab=ads\n• LinkedIn: https://www.linkedin.com/psettings/advertising\n• Twitter: https://twitter.com/settings/personalization'
        },
        {
          heading: 'reCAPTCHA',
          text: 'Google reCAPTCHA protects our forms from bots and spam. It analyzes user behavior and may set cookies.\n\nPrivacy Policy: https://policies.google.com/privacy'
        },
        {
          heading: 'Customer Support Chat',
          text: 'Our live support chat may use cookies to remember your conversation history and preferences.\n\nThese cookies help us provide better support by remembering your previous interactions.'
        }
      ]
    },
    'managing-cookies': {
      title: 'Managing Your Cookie Preferences',
      icon: 'Settings',
      content: [
        {
          heading: 'Browser Settings',
          text: 'All major browsers allow you to control cookies through their settings:\n\n• Chrome: Settings > Privacy and security > Cookies and other site data\n• Firefox: Settings > Privacy & Security > Cookies and Site Data\n• Safari: Preferences > Privacy > Cookies and website data\n• Edge: Settings > Privacy, search, and services > Cookies\n\nYou can block all cookies, block third-party cookies only, or delete cookies after each session.'
        },
        {
          heading: 'Cookie Preferences Center',
          text: 'When you first visit VA Sprouts, you\'ll see a cookie banner allowing you to accept or customize cookie preferences. You can change these preferences at any time through the "Cookie Preferences" link in our footer.'
        },
        {
          heading: 'Do Not Track Signals',
          text: 'Some browsers support "Do Not Track" (DNT) signals. Currently, there is no industry standard for how websites should respond to DNT signals. We honor your browser\'s cookie blocking settings but may not respond to DNT signals specifically.'
        },
        {
          heading: 'Mobile Devices',
          text: 'Mobile browsers also allow cookie management:\n\n• iOS Safari: Settings > Safari > Block All Cookies\n• Android Chrome: Settings > Site Settings > Cookies\n\nNote that disabling cookies on mobile may significantly limit platform functionality.'
        },
        {
          heading: 'Impact of Blocking Cookies',
          text: 'Disabling cookies may result in:\n\n• Inability to log in or maintain your session\n• Loss of personalized settings and preferences\n• Reduced functionality in messaging and real-time features\n• Slower performance due to lack of caching\n• Inability to use certain features like job applications\n• Repeated prompts for information\n\nWe recommend allowing essential cookies for the best experience.'
        }
      ]
    },
    'consent-compliance': {
      title: 'Cookie Consent & Compliance',
      icon: 'Shield',
      content: [
        {
          heading: 'GDPR Compliance (EU Users)',
          text: 'If you are located in the European Union, we comply with the General Data Protection Regulation (GDPR). We will:\n\n• Ask for your explicit consent before setting non-essential cookies\n• Provide clear information about what cookies we use\n• Allow you to withdraw consent at any time\n• Respect your cookie preferences\n• Not use cookies to process personal data without a legal basis'
        },
        {
          heading: 'CCPA Compliance (California Users)',
          text: 'California residents have rights under the California Consumer Privacy Act (CCPA). While cookies themselves may not always constitute "personal information," we provide transparency about the data collected through cookies and allow you to opt out of the sale of personal information (though we do not sell your data).'
        },
        {
          heading: 'ePrivacy Directive',
          text: 'We comply with the EU ePrivacy Directive (Cookie Law) by:\n\n• Obtaining consent before setting cookies\n• Providing clear information in this Cookie Policy\n• Offering an easy way to manage cookie preferences\n• Not using cookies for purposes other than those disclosed'
        },
        {
          heading: 'Cookie Consent Duration',
          text: 'Your cookie consent choice is stored for 12 months. After this period, we will ask for your consent again to ensure you remain informed and in control.'
        },
        {
          heading: 'Children\'s Privacy',
          text: 'VA Sprouts is not intended for users under 18. We do not knowingly collect data from children. If you believe a child has provided us with information, please contact us immediately.'
        }
      ]
    },
    'updates-contact': {
      title: 'Updates & Contact Information',
      icon: 'Mail',
      content: [
        {
          heading: 'Changes to This Policy',
          text: 'We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements. When we make significant changes, we will:\n\n• Update the "Last Updated" date below\n• Notify you through a banner or email\n• Request renewed consent if required by law\n\nYour continued use of the platform after changes indicates acceptance of the updated policy.'
        },
        {
          heading: 'Contact Us',
          text: 'If you have questions about our use of cookies or this Cookie Policy, please contact us:\n\nEmail: privacy@vasprouts.com\nAddress: VA Sprouts Privacy Team\n[Company Address]\n\nYou can also submit a privacy request through our Help Center or contact our Data Protection Officer for GDPR inquiries.'
        },
        {
          heading: 'Additional Resources',
          text: 'For more information about your privacy rights and our data practices, please review:\n\n• Privacy Policy\n• Terms of Service\n• Data Processing Agreement (for enterprise customers)\n• Cookie Preferences Center'
        },
        {
          heading: 'Last Updated',
          text: 'This Cookie Policy was last updated on February 9, 2026.'
        }
      ]
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSection(expandedSection === sectionKey ? null : sectionKey);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>Cookie Policy - VA Sprouts</title>
        <meta name="description" content="Learn about how VA Sprouts uses cookies and similar technologies to enhance your experience and protect your privacy." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <VAHeaderNav />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Icon name="FileText" size={48} className="text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            This Cookie Policy explains how VA Sprouts uses cookies and similar technologies 
            to enhance your experience, analyze usage, and protect your privacy.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last Updated: February 9, 2026
          </p>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="List" size={20} className="mr-2 text-primary-600" />
            Quick Navigation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => toggleSection(key)}
                className="text-left px-4 py-2 rounded-md hover:bg-primary-50 hover:text-primary-700 transition-colors text-sm flex items-center"
              >
                <Icon name={section.icon} size={16} className="mr-2" />
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Cookie Banner Example */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <Icon name="Info" size={24} className="text-primary-600 mr-3 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Cookie Preferences</h3>
              <p className="text-sm text-primary-800 mb-3">
                We use cookies to enhance your experience. You can manage your cookie preferences 
                through your browser settings or by clicking the "Cookie Settings" link in the footer.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/privacy-policy"
                  className="text-sm font-medium text-primary-700 hover:text-primary-800 underline"
                >
                  Privacy Policy
                </Link>
                <span className="text-primary-400">•</span>
                <Link
                  to="/terms-of-service"
                  className="text-sm font-medium text-primary-700 hover:text-primary-800 underline"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {Object.entries(sections).map(([key, section]) => (
            <div
              key={key}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(key)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Icon name={section.icon} size={24} className="text-primary-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                </div>
                <Icon
                  name={expandedSection === key ? 'ChevronUp' : 'ChevronDown'}
                  size={20}
                  className="text-gray-400"
                />
              </button>

              {expandedSection === key && (
                <div className="px-6 py-4 border-t border-gray-200 space-y-6">
                  {section.content.map((item, index) => (
                    <div key={index}>
                      <h3 className="font-semibold text-gray-900 mb-2">{item.heading}</h3>
                      <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white text-center">
          <Icon name="Shield" size={48} className="mx-auto mb-4 opacity-90" />
          <h2 className="text-2xl font-bold mb-3">Your Privacy Matters</h2>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            We are committed to transparency and giving you control over your data. 
            Review our full privacy policy or contact us with any questions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/privacy-policy"
              className="bg-white text-primary-700 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              View Privacy Policy
            </Link>
            <Link
              to="/help-support"
              className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-400 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 mb-3">Related Resources</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700 font-medium">
              Privacy Policy
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/terms-of-service" className="text-primary-600 hover:text-primary-700 font-medium">
              Terms of Service
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/help-support" className="text-primary-600 hover:text-primary-700 font-medium">
              Help Center
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/community-guidelines" className="text-primary-600 hover:text-primary-700 font-medium">
              Community Guidelines
            </Link>
          </div>
        </div>
      </main>

      <MainFooter />
    </div>
  );
};

export default CookiesPolicyPage;
